import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import type { RelayInfoDocument } from '@nostr/gadgets/relays'
import { HugeiconsIcon } from '@hugeicons/react'
import { CheckIcon, Copy01Icon } from '@hugeicons/core-free-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAtomValue } from 'jotai'
import { pool } from '@nostr/gadgets/global'
import { useAsync } from 'react-use'

export default function InviteCodeDialog({
  relayInfo,
  showInviteCodeDialog,
  setShowInviteCodeDialog
}: {
  relayInfo: RelayInfoDocument
  showInviteCodeDialog: boolean
  setShowInviteCodeDialog: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [copied, setCopied] = useState(false)

  const { value: inviteCode, loading: isFetching } = useAsync(async () => {
    if (!showInviteCodeDialog) return ''

    if (!relayInfo.self) return null

    try {
      const codeEvent = await pool.get([relayInfo.url], {
        kinds: [28935],
        authors: [relayInfo.self],
        limit: 1
      })

      if (!codeEvent) return null
      const claimTag = codeEvent.tags.find((tag: string[]) => tag[0] === 'claim')
      return claimTag?.[1] ?? null
    } catch (error: any) {
      console.warn(error.message || 'failed to get invite code')
      toast.error(error.message || t('Failed to get invite code'))
    }
  }, [showInviteCodeDialog])

  const handleCopyInviteCode = () => {
    if (!inviteCode) return

    navigator.clipboard.writeText(inviteCode)
    toast.success(t('Invite code copied to clipboard'))
    setCopied(true)

    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  const content = isFetching ? (
    <div className="flex items-center justify-center py-8">
      <div className="text-muted-foreground">{t('Loading...')}</div>
    </div>
  ) : inviteCode ? (
    <div className="space-y-2">
      <Label htmlFor="fetched-invite-code">{t('Invite Code')}</Label>
      <div className="flex gap-2">
        <Input id="fetched-invite-code" value={inviteCode} readOnly className="font-mono" />
        <Button onClick={handleCopyInviteCode} variant="outline">
          {copied ? <HugeiconsIcon icon={CheckIcon} /> : <HugeiconsIcon icon={Copy01Icon} />}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('This invite code can be used by others to join the relay.')}
      </p>
    </div>
  ) : (
    <div className="text-center py-8 text-muted-foreground">
      {t('No invite code available from this relay.')}
    </div>
  )

  if (isSmallScreen) {
    return (
      <Drawer open={showInviteCodeDialog} onOpenChange={setShowInviteCodeDialog}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t('Get Invite Code')}</DrawerTitle>
            <DrawerDescription>
              {t('Share this invite code with others to invite them to join this relay.')}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4">{content}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={showInviteCodeDialog} onOpenChange={setShowInviteCodeDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Get Invite Code')}</DialogTitle>
          <DialogDescription>
            {t('Share this invite code with others to invite them to join this relay.')}
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}
